import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { get } from 'lodash';
import { connect } from 'react-redux';
import Row from 'reactstrap/lib/Row';
import Col from 'reactstrap/lib/Col';
import Container from 'reactstrap/lib/Container';
import classnames from 'classnames';
import { useToggle } from 'site-modules/shared/hooks/use-toggle';

// Models
import { bindToContent, CmsEntities } from 'client/data/models/cms';

// Utils
import { connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { DEFAULT_CONTENT } from 'client/data/cms/content';
import { TrackingHandler } from 'client/tracking/handler';
import { getResearchCategoryLandingBreadcrumbs } from 'site-modules/editorial/utils/breadcrumbs';
import { getCategoryLandingContentPath } from 'site-modules/editorial/utils/content-path';
import { isTitleEvenCount } from 'site-modules/editorial/utils/homepage-ads-title-count';
import { scrollToHash } from 'client/utils/scroll';

// Constants
import { EditorialMap } from 'client/tracking/maps/editorial';
import { RESEARCH_LINK_WIDGET_EDITORIAL_PATH } from 'site-modules/shared/constants/editorial/research-article';
import { HomepageAdExtensionMap } from 'client/tracking/maps/common/homepage-ad-extension';

// Shared components
import { MrectNative } from 'site-modules/shared/components/native-ad/mrect/mrect-native';
import { NativeVideoAd } from 'site-modules/shared/components/native-ad/video/video-native-ad';
import { Breadcrumbs } from 'site-modules/shared/components/breadcrumbs/breadcrumbs';
import { PageLinksWidget } from 'site-modules/shared/components/page-links-widget/page-links-widget';
import { YoutubeVideoModalAsync } from 'site-modules/shared/components/youtube-video-modal/youtube-video-modal-async';
import { AnchorNavSticky } from 'site-modules/shared/components/anchor-nav-sticky/anchor-nav-sticky';

// Components
import { ResearchEntry } from 'site-modules/editorial/components/research-category-landing/research-entry/research-entry';

import './research-category-landing.scss';

function ResearchCategoryLandingUI(props) {
  const { content, category, splat, location, isMobile, linkWidgetContent } = props;
  const [videoModalData, setVideoModalData] = useState({});
  const [isVideoModalOpen, toggleVideoModalOpen] = useToggle(false);

  useEffect(() => {
    TrackingHandler.useMap({ ...EditorialMap, ...HomepageAdExtensionMap });

    const hash = get(location, 'hash', '');
    scrollToHash(hash, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onToggleVideoModal = useCallback(newVideoModalData => {
    toggleVideoModalOpen();
    if (get(newVideoModalData, 'videoId')) {
      setVideoModalData(newVideoModalData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const title = get(content, 'title', '');
  const subtitle = get(content, 'subtitle', '');
  const isEvenCount = isTitleEvenCount(title, subtitle);

  if (!content) {
    return null;
  }

  const { videoId, videoAspectRatio, creativeId: videoModalCreativeId, videoTitle } = videoModalData;

  const mainEntries = content.child('main').children();
  const rightRailEntries = content.child('right-rail').children();
  const footerEntries = content.child('footer').children();

  const subnavEntry = content.child('subnav');
  const anchorNavTitle = subnavEntry.metadata('subnavTitle').value();
  const anchorNavList = subnavEntry.links().map(({ href, title: name }) => ({
    name,
    hash: href.replace('#', ''),
  }));
  const breadcrumbsData = getResearchCategoryLandingBreadcrumbs({
    category,
    splat,
    title: content.metadata('breadcrumbTitle').value(content.title),
  });

  return (
    <main id="main-content" tabIndex={-1} className="research-category-landing">
      <Container>
        <Breadcrumbs className="ps-0 mb-2" data={breadcrumbsData} />
        <Row>
          <Col xs={12} lg={8}>
            <h1
              className={classnames('heading-2', {
                'mb-0_5': subtitle,
                'mb-1_5': !subtitle,
              })}
            >
              {title}
            </h1>
            {!!subtitle && <p className="size-16 mb-1_5 text-gray-darker">{subtitle}</p>}
            {!!anchorNavList.length && (
              <AnchorNavSticky className="mb-1_5" title={anchorNavTitle} navList={anchorNavList} isMobile={isMobile} />
            )}
            {mainEntries.map((entry, index) => (
              <ResearchEntry
                key={`main-${entry.id}`}
                entry={entry}
                pathname={location.pathname}
                openVideoModal={onToggleVideoModal}
                isMobile={isMobile}
                isTitleEvenCount={isEvenCount}
                linkWidgetContent={linkWidgetContent}
                hasSeparator={(index !== mainEntries.length - 1 || !!footerEntries.length) && !entry.id.includes('faq')}
              />
            ))}
          </Col>
          <Col xs={12} lg={4} className="d-flex flex-column justify-content-between">
            <div>
              {rightRailEntries.map(entry => (
                <ResearchEntry key={`right-rail-${entry.id}`} entry={entry} openVideoModal={onToggleVideoModal} />
              ))}
              <MrectNative position={'1'} refreshable={false} wiredOnly wiredBreakpoints={{ lg: true, xl: true }} />
            </div>
            {!footerEntries.length && <NativeVideoAd />}
            <MrectNative
              wrapperClass="mb-1_5"
              position={'2'}
              refreshable={false}
              renderWhenViewable
              wiredOnly
              wiredBreakpoints={{ lg: true, xl: true }}
            />
          </Col>
        </Row>
        <Row>
          <Col xs={12} lg={8}>
            {footerEntries.map((entry, index) => (
              <ResearchEntry
                key={`footer-${entry.id}`}
                entry={entry}
                openVideoModal={onToggleVideoModal}
                hasSeparator={index !== footerEntries.length - 1}
              />
            ))}
          </Col>
          {!!footerEntries.length && (
            <Col xs={12} lg={4} className="pb-1_5 d-flex flex-column justify-content-end">
              <NativeVideoAd />
            </Col>
          )}
        </Row>
        <PageLinksWidget divideSections={false} title="Related information" />
        <YoutubeVideoModalAsync
          onToggleModal={onToggleVideoModal}
          isOpen={isVideoModalOpen}
          videoId={videoId}
          videoAspectRatio={videoAspectRatio}
          creativeId={videoModalCreativeId}
          videoTitle={videoTitle}
        />
      </Container>
    </main>
  );
}

ResearchCategoryLandingUI.propTypes = {
  category: PropTypes.string.isRequired,
  splat: PropTypes.string,
  location: PropTypes.shape({
    pathname: PropTypes.string,
  }),
  content: CmsEntities.Content,
  linkWidgetContent: CmsEntities.Content,
  isMobile: PropTypes.bool,
};

ResearchCategoryLandingUI.defaultProps = {
  splat: null,
  location: {},
  content: null,
  linkWidgetContent: DEFAULT_CONTENT,
  isMobile: false,
};

export const mapStateToProps = state => ({
  isMobile: state.mobile,
});

export const ResearchCategoryLanding = connect(mapStateToProps)(
  connectToModel(ResearchCategoryLandingUI, {
    content: bindToContent(({ category, splat }) => getCategoryLandingContentPath(category, splat)),
    linkWidgetContent: bindToContent(RESEARCH_LINK_WIDGET_EDITORIAL_PATH),
  })
);
